import type {JsonValue} from '@github-ui/dotcom-schema/protobuf'
import {
  AgentMergePresentation_Kind as Kind,
  type AgentMergeEvaluationScope,
  type AgentMergePresentation,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'

import {sameScope} from './agent-merge-activity-reducer'

export class AgentMergeEvaluationScopeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AgentMergeEvaluationScopeError'
  }
}

export function isDecimalRevision(value: string, positive = false) {
  return /^(0|[1-9][0-9]*)$/.test(value) && (!positive || value !== '0')
}

export function compareRevisions(left: string, right: string) {
  if (!isDecimalRevision(left) || !isDecimalRevision(right)) throw new Error('Invalid Agent Merge status revision')
  const a = BigInt(left)
  const b = BigInt(right)
  return a < b ? -1 : a > b ? 1 : 0
}

export function validEvaluationScope(scope: AgentMergeEvaluationScope) {
  return (
    !!scope.scope?.namespace &&
    scope.scope.repositoryId > 0n &&
    scope.scope.pullRequestNumber > 0n &&
    scope.scope.pullRequestId > 0n &&
    isDecimalRevision(scope.repositoryOwnerId, true) &&
    ['user', 'organization'].includes(scope.repositoryOwnerType) &&
    (!scope.ownerUserId || isDecimalRevision(scope.ownerUserId, true)) &&
    (!scope.configurationGeneration || isDecimalRevision(scope.configurationGeneration, true)) &&
    (!scope.displayGeneration || isDecimalRevision(scope.displayGeneration))
  )
}

export function evaluationScopeKey(scope: AgentMergeEvaluationScope) {
  return JSON.stringify([
    scope.scope?.namespace,
    scope.scope?.repositoryId.toString(),
    scope.scope?.pullRequestNumber.toString(),
    scope.scope?.pullRequestId.toString(),
    scope.repositoryOwnerId,
    scope.repositoryOwnerType,
    scope.ownerUserId,
    scope.configurationGeneration,
    scope.headOid,
    scope.baseOid,
    scope.displayGeneration,
  ])
}

export function sameEvaluationConfigurationRecord(left: AgentMergeEvaluationScope, right: AgentMergeEvaluationScope) {
  return (
    sameScope(left.scope, right.scope) &&
    left.repositoryOwnerId === right.repositoryOwnerId &&
    left.repositoryOwnerType === right.repositoryOwnerType
  )
}

export function validPresentation(presentation?: AgentMergePresentation, allowBusy = true) {
  return (
    !!presentation &&
    presentation.kind >= Kind.NEUTRAL &&
    presentation.kind <= (allowBusy ? Kind.BUSY : Kind.CRITICAL) &&
    /^[a-z0-9_.-]{1,96}$/.test(presentation.code) &&
    !!presentation.message.trim() &&
    !/\p{Cc}/u.test(presentation.message) &&
    new TextEncoder().encode(presentation.message).length <= 512
  )
}

function object(value: JsonValue | undefined) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined
}

export function isEvaluationStatusInvalidation(data: JsonValue, expected: AgentMergeEvaluationScope) {
  const notification = object(data)
  if (object(notification?.event_updates)?.agent_merge_evaluation_status_updated !== true) return false
  const scope = object(notification?.agent_merge_evaluation_status)
  return (
    !!scope &&
    typeof scope.tenant_id === 'string' &&
    expected.scope?.namespace.slice(expected.scope.namespace.lastIndexOf(':') + 1) === (scope.tenant_id || 'dotcom') &&
    scope.repository_id === expected.scope.repositoryId.toString() &&
    scope.pull_request_number === expected.scope.pullRequestNumber.toString() &&
    scope.pull_request_id === expected.scope.pullRequestId.toString() &&
    typeof scope.configuration_generation === 'string' &&
    isDecimalRevision(scope.configuration_generation, true) &&
    (!expected.configurationGeneration ||
      compareRevisions(scope.configuration_generation, expected.configurationGeneration) >= 0) &&
    typeof scope.revision === 'string' &&
    isDecimalRevision(scope.revision, true) &&
    (scope.head_sha === undefined || scope.head_sha === expected.headOid)
  )
}

export function hasEvaluationStatusMarker(data: JsonValue) {
  return object(object(data)?.event_updates)?.agent_merge_evaluation_status_updated === true
}
